/**
 * Top-level error fallback. Wraps the router so a thrown render error
 * doesn't blank the screen. Logs to console (Sentry would hook in here
 * later via `init_sentry`-style integration).
 */
import { type ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { Button } from '@/components/ui/button/button';

import styles from './error-boundary.module.css';

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  const message = error instanceof Error ? error.message : 'Something broke.';

  return (
    <main className={styles.wrap} role="alert">
      <div className={styles.card}>
        <div className={styles.badge}>Error</div>
        <h1 className={styles.title}>Something went sideways.</h1>
        <p className={styles.message}>{message}</p>
        <Button variant="primary" size="lg" onClick={resetErrorBoundary}>
          Try again
        </Button>
      </div>
    </main>
  );
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={Fallback}
      onError={(error, info) => {
        console.error('Caught by AppErrorBoundary:', error, info);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
